//src/components/assessments/StudentMarks.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  FormControlLabel
} from '@mui/material';
import { Refresh as RefreshIcon, Save as SaveIcon, Search as SearchIcon, CloudDownload, CloudUpload, Description, Check, Error as ErrorIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import { academicAPI, assessmentAPI, reportCardAPI, schoolAPI, studentAPI } from '../../services/api';
import MarksListPdfDoc from './MarksListPdfDoc';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';

const extractCollection = (responseOrPayload) => {
  const payload = responseOrPayload?.data !== undefined ? responseOrPayload.data : responseOrPayload;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const getErrorMessage = (error, fallback = 'Operation failed') => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback
);

const getSubjectName = (subject) => (
  subject?.subject_name ||
  subject?.name ||
  'Subject N/A'
);

const getStudentName = (student) => (
  student?.full_name ||
  `${student?.last_name || ''} ${student?.first_name || ''}`.trim() ||
  student?.registration_no ||
  `Student #${student?.id}`
);

const getConfigLabel = (config) => {
  const assessmentType = config?.assessment_type?.assessment_name || 'Assessment';
  const className = config?.class_stream_subject_paper?.class?.name || 'Class N/A';
  const streamName = config?.class_stream_subject_paper?.stream?.name || 'No Stream';
  const subject = getSubjectName(config?.class_stream_subject_paper?.subject_paper?.subject);
  const paper = config?.class_stream_subject_paper?.subject_paper?.paper_name || '';
  return `${assessmentType} | ${className} - ${streamName} | ${subject}${paper ? ` - ${paper}` : ''}`;
};

const responsiveAutocompleteSx = {
  width: '100%',
  minWidth: {
    sm: 240,
    lg: 280
  }
};

const EMPTY_MARK = {
  id: null,
  marks_obtained: '',
  is_absent: false,
  is_exempted: false,
  remarks: ''
};

/**
 * After saving marks, auto-create/update subject comments from 'Subject' templates.
 * @param {Array} savedEntries  - [{ student_id, marks_obtained, is_absent, is_exempted }]
 * @param {object} config       - selectedConfigData (has max_marks, class_stream_subject_paper)
 * @param {object} termInfo     - { id, academic_year }
 */
const autoGenerateSubjectComments = async (savedEntries, config, termInfo) => {
  const subjectPaper = config?.class_stream_subject_paper?.subject_paper;
  const subjectId = subjectPaper?.subject?.id || subjectPaper?.subject_id;
  if (!subjectId || !termInfo?.id) return;

  const maxMarks = Number(config.max_marks || 0);
  if (!maxMarks) return;

  let templates;
  try {
    const res = await reportCardAPI.getCommentTemplates({ comment_type: 'Subject' });
    templates = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
  } catch { return; }

  if (!templates.length) return;

  const eligible = savedEntries.filter(
    (e) => !e.is_absent && !e.is_exempted && e.marks_obtained !== null && e.marks_obtained !== ''
  );

  await Promise.allSettled(eligible.map(async (entry) => {
    const pct = (Number(entry.marks_obtained) / maxMarks) * 100;
    const match = templates.find((t) => {
      const b = t.grade_boundary;
      if (!b) return false;
      return pct >= Number(b.min_percentage) && pct <= Number(b.max_percentage);
    });
    if (!match) return;
    await reportCardAPI.autoGenerateSubjectComment({
      student_id: Number(entry.student_id),
      subject_id: Number(subjectId),
      term_id: Number(termInfo.id),
      academic_year: Number(termInfo.academic_year),
      comment_text: match.comment_text,
    });
  }));
};

const StudentMarks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [students, setStudents] = useState([]);
  const [assessmentConfigs, setAssessmentConfigs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [allowedClassIds, setAllowedClassIds] = useState(null); // null = no restriction
  const [allowedPaperIds, setAllowedPaperIds] = useState(null); // null = no restriction
  const [classTeacherClassIds, setClassTeacherClassIds] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedAssessmentType, setSelectedAssessmentType] = useState('');
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear());
  const [marks, setMarks] = useState({});
  const [savedMarks, setSavedMarks] = useState({});
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingStudent, setSavingStudent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const selectedConfigData = useMemo(
    () => assessmentConfigs.find((config) => Number(config.id) === Number(selectedConfig)),
    [assessmentConfigs, selectedConfig]
  );

  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) {
      // Sort students by their marks (highest first)
      const studentsWithMarks = students.map(student => ({
        student,
        mark: marks[student.id]?.marks_obtained ?? -1
      }));
      studentsWithMarks.sort((a, b) => {
        // Students with marks come first
        if (b.mark !== a.mark) return b.mark - a.mark;
        // If no marks or same marks, sort alphabetically
        return getStudentName(a.student).localeCompare(getStudentName(b.student));
      });
      return studentsWithMarks.map(item => item.student);
    }
    const term = searchTerm.toLowerCase();
    return students.filter((student) => {
      const name = getStudentName(student).toLowerCase();
      const regNo = (student?.registration_no || '').toLowerCase();
      return name.includes(term) || regNo.includes(term);
    });
  }, [students, searchTerm, marks]);

  const pagination = useMemo(() => {
    const total = filteredStudents.length;
    const last_page = Math.max(1, Math.ceil(total / perPage));
    const current_page = Math.min(page, last_page);
    return { total, per_page: perPage, current_page, last_page };
  }, [filteredStudents.length, page, perPage]);

  const pagedStudents = useMemo(() => {
    const start = (pagination.current_page - 1) * perPage;
    return filteredStudents.slice(start, start + perPage);
  }, [filteredStudents, pagination.current_page, perPage]);

  const fetchFilters = useCallback(async () => {
    setLoadingFilters(true);
    try {
      const [classesResponse, termsResponse, typesResponse, myClassesResponse, myPapersResponse, myCtClassesResponse] = await Promise.all([
        academicAPI.getClasses(),
        academicAPI.getTerms(),
        assessmentAPI.getActiveAssessmentTypes(),
        assessmentAPI.getMyClasses(),
        assessmentAPI.getMySubjectPapers(),
        assessmentAPI.getMyClassTeacherClasses().catch(() => ({ data: [] }))
      ]);

      const allClasses = extractCollection(classesResponse);

      const myClassIds = myClassesResponse.data?.manage_all === true ? null
        : (myClassesResponse.data?.class_ids ?? myClassesResponse.data ?? []);
      const myPaperIds = myPapersResponse.data?.manage_all === true ? null
        : (Array.isArray(myPapersResponse.data) ? myPapersResponse.data : []);
      const ctClassIds = Array.isArray(myCtClassesResponse.data) ? myCtClassesResponse.data.map(Number) : [];


      setAllowedClassIds(myClassIds);
      setAllowedPaperIds(myPaperIds);
      setClassTeacherClassIds(ctClassIds);

      const visibleClasses = myClassIds === null
        ? allClasses
        : allClasses.filter((c) => myClassIds.map(Number).includes(Number(c.id)));

      setClasses(visibleClasses);
      const fetchedTerms = extractCollection(termsResponse);
      setTerms(fetchedTerms);
      setAssessmentTypes(extractCollection(typesResponse));

      // Auto-select current term
      const currentTerm = fetchedTerms.find((t) => t.is_current || t.current);
      if (currentTerm && !selectedTerm) {
        setSelectedTerm(currentTerm.id);
        setAcademicYear(currentTerm.year || currentTerm.academic_year || new Date().getFullYear());
      }

      // Auto-select class: prefer class teacher's class, else first visible class
      if (!selectedClass && visibleClasses.length > 0) {
        const ctClass = ctClassIds.length > 0
          ? visibleClasses.find((c) => ctClassIds.includes(Number(c.id)))
          : null;
        setSelectedClass((ctClass || visibleClasses[0]).id);
      }
    } catch (error) {
      console.error('Error fetching classes/terms:', error);
      setClasses([]);
      setTerms([]);
      setSnackbar({ open: true, message: 'Failed to load filters', severity: 'error' });
    } finally {
      setLoadingFilters(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAssessmentConfigs = useCallback(async () => {
    if (!selectedClass || !selectedTerm || !academicYear) {
      setAssessmentConfigs([]);
      setSelectedConfig('');
      return;
    }

    setLoadingConfigs(true);
    try {
      const response = await assessmentAPI.getAssessmentConfigurations({
        academic_year: academicYear,
        term_id: selectedTerm,
        is_active: true,
        per_page: 1000
      });

      const allConfigs = extractCollection(response);
      const filteredConfigs = allConfigs.filter((config) => {
        const configClassId = config?.class_stream_subject_paper?.class_id;
        const configTypeId = config?.assessment_type_id || config?.assessment_type?.id;
        const paperId = config?.class_stream_subject_paper_id || config?.class_stream_subject_paper?.id;

        // No restriction for manage_all users
        const isClassAllowed = allowedClassIds === null || allowedClassIds.map(Number).includes(Number(configClassId));
        const isPaperAllowed = allowedPaperIds === null || allowedPaperIds.map(Number).includes(Number(paperId));
        // Class teachers see all assessments in their class; subject teachers see only their papers
        const allowed = isClassAllowed || isPaperAllowed;

        return (
          Number(configClassId) === Number(selectedClass) &&
          (!selectedAssessmentType || Number(configTypeId) === Number(selectedAssessmentType)) &&
          allowed
        );
      });
      setAssessmentConfigs(filteredConfigs);

      if (selectedConfig && !filteredConfigs.some((config) => Number(config.id) === Number(selectedConfig))) {
        setSelectedConfig('');
      }

      // Auto-select first assessment type if none selected
      if (!selectedAssessmentType && filteredConfigs.length > 0) {
        const firstTypeId = filteredConfigs[0]?.assessment_type_id || filteredConfigs[0]?.assessment_type?.id;
        if (firstTypeId) setSelectedAssessmentType(firstTypeId);
      }

      // Auto-select first config if none selected
      if (!selectedConfig && filteredConfigs.length > 0) {
        setSelectedConfig(filteredConfigs[0].id);
      }
    } catch (error) {
      console.error('Error fetching assessment configurations:', error);
      setAssessmentConfigs([]);
      setSnackbar({ open: true, message: getErrorMessage(error, 'Failed to load assessments'), severity: 'error' });
    } finally {
      setLoadingConfigs(false);
    }
  }, [academicYear, selectedClass, selectedConfig, selectedTerm, selectedAssessmentType, allowedPaperIds, allowedClassIds]);

  const assessmentConfigsRef = React.useRef(assessmentConfigs);
  useEffect(() => { assessmentConfigsRef.current = assessmentConfigs; }, [assessmentConfigs]);

  const fetchStudentsAndMarks = useCallback(async (assessmentConfigId, resetPage = false) => {
    const config = assessmentConfigsRef.current.find((item) => Number(item.id) === Number(assessmentConfigId));
    const classId = config?.class_stream_subject_paper?.class_id;
    const streamId = config?.class_stream_subject_paper?.stream_id;

    if (!classId) {
      setStudents([]);
      setMarks({});
      setSnackbar({ open: true, message: 'Selected assessment has no class assignment', severity: 'error' });
      return;
    }

    setLoadingStudents(true);
    try {
      const [studentsResponse, marksResponse] = await Promise.all([
        studentAPI.getActive({
          class_id: classId,
          ...(streamId ? { stream_id: streamId } : {}),
          per_page: 1000
        }),
        assessmentAPI.getMarksForAssessment(assessmentConfigId)
      ]);

      const studentsList = extractCollection(studentsResponse);
      const marksList = extractCollection(marksResponse);

      const nextMarks = {};
      studentsList.forEach((student) => {
        nextMarks[student.id] = { ...EMPTY_MARK };
      });

      marksList.forEach((mark) => {
        if (!mark?.student_id) return;
        nextMarks[mark.student_id] = {
          ...(nextMarks[mark.student_id] || { ...EMPTY_MARK }),
          id: mark.id || null,
          marks_obtained: mark.marks_obtained ?? '',
          is_absent: Boolean(mark.is_absent),
          is_exempted: Boolean(mark.is_exempted),
          remarks: mark.remarks || ''
        };
      });

      setStudents(studentsList);
      setMarks(nextMarks);
      setSavedMarks(nextMarks);
      if (resetPage) setPage(1);
    } catch (error) {
      console.error('Error fetching students and marks:', error);
      setStudents([]);
      setMarks({});
      setSnackbar({ open: true, message: getErrorMessage(error, 'Failed to load students and marks'), severity: 'error' });
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    if (selectedClass && selectedTerm && academicYear) {
      fetchAssessmentConfigs();
    } else {
      setAssessmentConfigs([]);
      setSelectedConfig('');
      setStudents([]);
      setMarks({});
    }
  }, [academicYear, fetchAssessmentConfigs, selectedClass, selectedTerm, selectedAssessmentType]);

  useEffect(() => {
    if (selectedConfig) {
      fetchStudentsAndMarks(selectedConfig, true);
    } else {
      setStudents([]);
      setMarks({});
    }
  }, [fetchStudentsAndMarks, selectedConfig]);

  const hasUnsavedChanges = useMemo(() => {
    return Object.keys(marks).some((studentId) => {
      const cur = marks[studentId];
      const sav = savedMarks[studentId];
      if (!sav) return String(cur.marks_obtained) !== '' || cur.is_absent || cur.is_exempted || cur.remarks !== '';
      return (
        String(cur.marks_obtained) !== String(sav.marks_obtained) ||
        cur.is_absent !== sav.is_absent ||
        cur.is_exempted !== sav.is_exempted ||
        cur.remarks !== sav.remarks
      );
    });
  }, [marks, savedMarks]);

  const handleMarksKeyDown = (e, studentId) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    e.preventDefault();
    const inputs = Array.from(document.querySelectorAll('[data-marks-input]'));
    const idx = inputs.findIndex((el) => el.dataset.marksInput === String(studentId));
    if (idx === -1) return;
    const next = e.key === 'ArrowUp' ? inputs[idx - 1] : inputs[idx + 1];
    next?.focus();
  };

  const handleMarkChange = (studentId, field, value) => {
    setMarks((prev) => {
      const current = prev[studentId] || { ...EMPTY_MARK };
      const next = { ...current, [field]: value };

      if (field === 'is_absent' && value) {
        next.is_exempted = false;
        next.marks_obtained = '';
      }

      if (field === 'is_exempted' && value) {
        next.is_absent = false;
        next.marks_obtained = '';
      }

      return {
        ...prev,
        [studentId]: next
      };
    });
  };

  const handleSaveSingle = async (student) => {
    const maxMarks = Number(selectedConfigData.max_marks || 0);
    const row = marks[student.id] || EMPTY_MARK;
    const rawMark = row.marks_obtained;
    const numericMark = rawMark === '' || rawMark === null ? null : Number(rawMark);

    if (numericMark !== null && (Number.isNaN(numericMark) || numericMark < 0 || numericMark > maxMarks)) {
      setSnackbar({ open: true, message: `Marks must be between 0 and ${maxMarks}`, severity: 'error' });
      return;
    }

    setSavingStudent(student.id);
    try {
      const response = await assessmentAPI.bulkCreateStudentMarks({
        assessment_configuration_id: Number(selectedConfig),
        marks: [{
          student_id: Number(student.id),
          marks_obtained: row.is_absent || row.is_exempted ? null : numericMark,
          is_absent: Boolean(row.is_absent),
          is_exempted: Boolean(row.is_exempted),
          remarks: row.remarks || ''
        }]
      });
      const detail = (response?.data?.data?.details || []).find((d) => d.status === 'success');
      if (detail) {
        setMarks((prev) => {
          const next = { ...prev, [student.id]: { ...prev[student.id], id: detail.marks_id } };
          setSavedMarks(next);
          return next;
        });
      }
      setSnackbar({ open: true, message: 'Mark saved', severity: 'success' });
      const termObj = terms.find((t) => Number(t.id) === Number(selectedTerm));
      autoGenerateSubjectComments(
        [{ student_id: Number(student.id), marks_obtained: row.is_absent || row.is_exempted ? null : numericMark, is_absent: row.is_absent, is_exempted: row.is_exempted }],
        selectedConfigData,
        { id: Number(termObj?.id), academic_year: Number(termObj?.academic_year || termObj?.year || academicYear) }
      );
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error, 'Failed to save mark'), severity: 'error' });
    } finally {
      setSavingStudent(null);
    }
  };

  const handleExportPdf = async () => {
    setDownloadingPdf(true);
    try {
      const schoolRes = await schoolAPI.get().catch(() => null);
      const school = schoolRes?.data?.data || schoolRes?.data || null;
      const blob = await pdf(
        <MarksListPdfDoc
          students={students}
          marks={marks}
          configData={selectedConfigData}
          title={`marks_${selectedConfig}`}
          school={school}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `marks_pdf_${selectedConfig}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to generate PDF', severity: 'error' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportTemplate = async () => {
    setExportingFormat('template');
    try {
      const response = await assessmentAPI.downloadMarksTemplate(selectedConfig);
      downloadBlob(response.data, `marks_template_${selectedConfig}.xlsx`);
    } catch {
      setSnackbar({ open: true, message: 'Failed to download template', severity: 'error' });
    } finally {
      setExportingFormat(null);
    }
  };

  const handleExportWithMarks = async () => {
    setExportingFormat('marks');
    try {
      const response = await assessmentAPI.exportMarks(selectedConfig);
      downloadBlob(response.data, `marks_export_${selectedConfig}.xlsx`);
    } catch {
      setSnackbar({ open: true, message: 'Failed to export marks', severity: 'error' });
    } finally {
      setExportingFormat(null);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConfig) return;
    e.target.value = '';

    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!['.xlsx', '.xls'].includes(ext)) {
      setSnackbar({ open: true, message: 'Please upload an Excel file (.xlsx or .xls)', severity: 'error' });
      return;
    }

    setImporting(true);
    try {
      const response = await assessmentAPI.importMarks(selectedConfig, file);
      const details = response?.data?.data?.details || [];
      const successCount = details.filter((d) => d.status === 'success').length;
      const failedDetails = details.filter((d) => d.status !== 'success');
      const studentNameMap = Object.fromEntries(students.map((s) => [String(s.id), getStudentName(s)]));
      const allErrors = [
        ...(response?.data?.validation_errors || []),
        ...failedDetails.map((d) => ({
          row: studentNameMap[String(d.student_id)] || `Student #${d.student_id}`,
          message: d.error || 'Failed to save',
        })),
      ];
      setImportResults({ success_count: successCount, error_count: allErrors.length, errors: allErrors });
      if (successCount > 0) fetchStudentsAndMarks(selectedConfig);
    } catch (error) {
      setImportResults({ success_count: 0, error_count: 1, errors: [{ row: 'N/A', message: getErrorMessage(error, 'Import failed') }] });
    } finally {
      setImporting(false);
    }
  };

  const handleBulkSave = async () => {
    if (!selectedConfigData) {
      setSnackbar({ open: true, message: 'Please select an assessment first', severity: 'error' });
      return;
    }

    if (students.length === 0) {
      setSnackbar({ open: true, message: 'No students found for this assessment', severity: 'error' });
      return;
    }

    const maxMarks = Number(selectedConfigData.max_marks || 0);
    const marksData = students.map((student) => {
      const row = marks[student.id] || EMPTY_MARK;
      const rawMark = row.marks_obtained;
      const numericMark = rawMark === '' || rawMark === null || rawMark === undefined ? null : Number(rawMark);

      return {
        student_id: Number(student.id),
        marks_obtained: row.is_absent || row.is_exempted ? null : numericMark,
        is_absent: Boolean(row.is_absent),
        is_exempted: Boolean(row.is_exempted),
        remarks: row.remarks || ''
      };
    });

    const invalidEntry = marksData.find((entry) => (
      entry.marks_obtained !== null &&
      (Number.isNaN(entry.marks_obtained) || entry.marks_obtained < 0 || entry.marks_obtained > maxMarks)
    ));
    if (invalidEntry) {
      setSnackbar({
        open: true,
        message: `Marks must be between 0 and ${maxMarks}`,
        severity: 'error'
      });
      return;
    }

    setSaving(true);
    try {
      const response = await assessmentAPI.bulkCreateStudentMarks({
        assessment_configuration_id: Number(selectedConfig),
        marks: marksData
      });
      // Update marks state with returned IDs — no re-fetch, no page reset
      const details = response?.data?.data?.details || [];
      setMarks((prev) => {
        const next = { ...prev };
        details.forEach(({ student_id, marks_id, status }) => {
          if (status === 'success' && next[student_id]) {
            next[student_id] = { ...next[student_id], id: marks_id };
          }
        });
        setSavedMarks(next);
        return next;
      });
      setSnackbar({ open: true, message: 'Marks saved successfully', severity: 'success' });
      const termObj = terms.find((t) => Number(t.id) === Number(selectedTerm));
      autoGenerateSubjectComments(
        marksData,
        selectedConfigData,
        { id: Number(termObj?.id), academic_year: Number(termObj?.academic_year || termObj?.year || academicYear) }
      );
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error, 'Failed to save marks'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <ModulePageHeader
        title="Student Marks Entry"
        subtitle="Select class and term, then enter marks for a specific assessment configuration"
        actions={[
          <Button
            key="refresh"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchFilters}
            disabled={loadingFilters || saving}
            fullWidth={isMobile}
          >
            Refresh
          </Button>
        ]}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Academic Year"
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                inputProps={{ min: 2000, max: 2100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                options={terms}
                value={terms.find((term) => Number(term.id) === Number(selectedTerm)) || null}
                onChange={(_, value) => setSelectedTerm(value?.id || '')}
                getOptionLabel={(option) => `${option.name || 'Term'} ${option.year || ''}`.trim()}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={loadingFilters}
                renderInput={(params) => <TextField {...params} label="Term" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                options={classes}
                value={classes.find((schoolClass) => Number(schoolClass.id) === Number(selectedClass)) || null}
                onChange={(_, value) => setSelectedClass(value?.id || '')}
                getOptionLabel={(option) => option.name || option.class_name || 'Class'}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={loadingFilters}
                renderInput={(params) => <TextField {...params} label="Class" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                options={assessmentTypes}
                value={assessmentTypes.find((t) => Number(t.id) === Number(selectedAssessmentType)) || null}
                onChange={(_, value) => { setSelectedAssessmentType(value?.id || ''); setSelectedConfig(''); }}
                getOptionLabel={(option) => option.assessment_name || option.name || 'Type'}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={loadingFilters}
                renderInput={(params) => <TextField {...params} label="Assessment Type" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={3}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                options={assessmentConfigs}
                value={assessmentConfigs.find((config) => Number(config.id) === Number(selectedConfig)) || null}
                onChange={(_, value) => setSelectedConfig(value?.id || '')}
                getOptionLabel={(option) => getConfigLabel(option)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!selectedClass || !selectedTerm || !selectedAssessmentType || loadingConfigs}
                renderInput={(params) => <TextField {...params} label="Assessment" size="small" />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedConfigData && (
        <Card>
          <CardContent>
            <Box mb={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by student name or registration number..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 1.5 }}
              />
              <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap" alignItems="center">
                <Chip label={`Max: ${selectedConfigData.max_marks}`} color="primary" size="small" />
                <Chip label={`Weight: ${selectedConfigData.weight_percentage}%`} color="secondary" size="small" />
                <Chip label={`Total: ${filteredStudents.length}`} size="small" />
                <Chip label={`Marked: ${Object.values(marks).filter(m => m.id !== null).length}`} color="success" size="small" />
                <Chip label={`Unmarked: ${filteredStudents.length - Object.values(marks).filter(m => m.id !== null).length}`} color="warning" size="small" />
                {hasUnsavedChanges && (
                  <Chip label="Unsaved changes" color="warning" size="small" variant="filled" />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={downloadingPdf ? <CircularProgress size={14} /> : <PdfIcon />}
                  onClick={handleExportPdf}
                  disabled={downloadingPdf || exportingFormat !== null || importing || students.length === 0}
                >
                  {downloadingPdf ? 'Downloading...' : 'Export PDF'}
                </Button>
                <ButtonGroup size="small" variant="outlined" color="success">
                  <Button
                    startIcon={exportingFormat === 'template' ? <CircularProgress size={14} /> : <Description />}
                    onClick={handleExportTemplate}
                    disabled={exportingFormat !== null || importing || students.length === 0}
                  >
                    Template
                  </Button>
                  <Button
                    startIcon={exportingFormat === 'marks' ? <CircularProgress size={14} /> : <CloudDownload />}
                    onClick={handleExportWithMarks}
                    disabled={exportingFormat !== null || importing || students.length === 0}
                  >
                    Export
                  </Button>
                </ButtonGroup>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  component="label"
                  startIcon={importing ? <CircularProgress size={14} /> : <CloudUpload />}
                  disabled={importing || exportingFormat !== null || !selectedConfig}
                >
                  {importing ? 'Importing...' : 'Import'}
                  <input type="file" accept=".xlsx,.xls" onChange={handleImport} hidden />
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleBulkSave}
                  disabled={saving || loadingStudents || students.length === 0}
                  size="small"
                >
                  {saving ? 'Saving...' : 'Save All'}
                </Button>
              </Box>
            </Box>

            {loadingStudents ? (
              <Box py={5} textAlign="center">
                <CircularProgress size={28} />
              </Box>
            ) : filteredStudents.length === 0 ? (
              <EmptyRecordsState message={searchTerm ? 'No students match your search.' : 'No students found for the selected class/stream.'} />
            ) : isMobile ? (
              <>
                <ResponsiveRecordList
                  items={pagedStudents}
                  renderItem={(student, index) => {
                    const globalIndex = (pagination.current_page - 1) * perPage + index;
                    const row = marks[student.id] || EMPTY_MARK;
                    const disabledMarkField = row.is_absent || row.is_exempted || saving;

                    return (
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography fontWeight={700}>{globalIndex + 1}. {getStudentName(student)}</Typography>
                          <Typography variant="body2" color="text.secondary">{student?.registration_no || '-'}</Typography>
                        </Box>
                        <TextField
                          size="small"
                          type="number"
                          label="Marks"
                          value={row.marks_obtained}
                          onChange={(e) => handleMarkChange(student.id, 'marks_obtained', e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          inputProps={{ min: 0, max: selectedConfigData.max_marks, step: 0.5 }}
                          disabled={disabledMarkField}
                        />
                        <Box display="flex" gap={1} flexWrap="wrap">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(row.is_absent)}
                                onChange={(e) => handleMarkChange(student.id, 'is_absent', e.target.checked)}
                                disabled={saving}
                              />
                            }
                            label="Absent"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(row.is_exempted)}
                                onChange={(e) => handleMarkChange(student.id, 'is_exempted', e.target.checked)}
                                disabled={saving}
                              />
                            }
                            label="Exempt"
                          />
                        </Box>
                        <TextField
                          size="small"
                          label="Remarks"
                          value={row.remarks}
                          onChange={(e) => handleMarkChange(student.id, 'remarks', e.target.value)}
                          disabled={saving}
                          placeholder="Optional notes..."
                          fullWidth
                        />
                      </Stack>
                    );
                  }}
                />
                <TablePagination
                  pagination={pagination}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => { setPerPage(size); setPage(1); }}
                  isMobile
                />
              </>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width={50}>#</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width={200}>Student Name</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width={100}>Reg No</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width={120}>Class</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width={80}>Gender</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width={120}>Marks</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold', textAlign: 'center' }} width={80}>Absent</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold', textAlign: 'center' }} width={80}>Exempt</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>Remarks</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }} width={70}>Save</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedStudents.map((student, index) => {
                        const globalIndex = (pagination.current_page - 1) * perPage + index;
                        const row = marks[student.id] || EMPTY_MARK;
                        const disabledMarkField = row.is_absent || row.is_exempted || saving;
                        const hasSavedMark = row.id !== null;
                        return (
                          <TableRow key={student.id} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell sx={{ py: 1 }}>{globalIndex + 1}</TableCell>
                            <TableCell sx={{ py: 1, fontWeight: 500 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {getStudentName(student)}
                                {hasSavedMark && <Chip label="Marked" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1, color: 'text.secondary', fontSize: '0.75rem' }}>{student?.registration_no || '-'}</TableCell>
                            <TableCell sx={{ py: 1, fontSize: '0.75rem' }}>{student?.class_stream?.school_class?.name || '-'}</TableCell>
                            <TableCell sx={{ py: 1, fontSize: '0.75rem', textTransform: 'capitalize' }}>{student?.gender || '-'}</TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={row.marks_obtained}
                                onChange={(e) => handleMarkChange(student.id, 'marks_obtained', e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                onKeyDown={(e) => handleMarksKeyDown(e, student.id)}
                                inputProps={{
                                  min: 0,
                                  max: selectedConfigData.max_marks,
                                  step: 0.5,
                                  style: { textAlign: 'center', fontSize: '0.875rem' },
                                  'data-marks-input': String(student.id),
                                }}
                                disabled={disabledMarkField}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: disabledMarkField ? 'grey.100' : hasSavedMark ? 'success.50' : 'white',
                                    '&:hover': { bgcolor: disabledMarkField ? 'grey.100' : hasSavedMark ? 'success.100' : 'grey.50' }
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.5 }}>
                              <Checkbox
                                size="small"
                                checked={Boolean(row.is_absent)}
                                onChange={(e) => handleMarkChange(student.id, 'is_absent', e.target.checked)}
                                disabled={saving}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.5 }}>
                              <Checkbox
                                size="small"
                                checked={Boolean(row.is_exempted)}
                                onChange={(e) => handleMarkChange(student.id, 'is_exempted', e.target.checked)}
                                disabled={saving}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={row.remarks}
                                onChange={(e) => handleMarkChange(student.id, 'remarks', e.target.value)}
                                disabled={saving || savingStudent === student.id}
                                placeholder="Optional notes..."
                                sx={{ '& .MuiOutlinedInput-input': { fontSize: '0.8rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color={hasSavedMark ? 'success' : 'primary'}
                                onClick={() => handleSaveSingle(student)}
                                disabled={saving || savingStudent !== null}
                                sx={{ minWidth: 0, px: 1 }}
                              >
                                {savingStudent === student.id ? <CircularProgress size={14} /> : <SaveIcon fontSize="small" />}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  pagination={pagination}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => { setPerPage(size); setPage(1); }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!importResults} onClose={() => setImportResults(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {importResults?.error_count > 0 ? <ErrorIcon color="warning" /> : <Check color="success" />}
            Import Results
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: 'success.light' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, '&:last-child': { pb: 2 } }}>
                  <Check sx={{ fontSize: 32 }} />
                  <Box>
                    <Typography variant="h5">{importResults?.success_count || 0}</Typography>
                    <Typography variant="body2">Imported</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: 'error.light' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, '&:last-child': { pb: 2 } }}>
                  <ErrorIcon sx={{ fontSize: 32 }} />
                  <Box>
                    <Typography variant="h5">{importResults?.error_count || 0}</Typography>
                    <Typography variant="body2">Failed</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {importResults?.errors?.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={60} sx={{ fontWeight: 'bold' }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResults.errors.map((err, i) => (
                    <TableRow key={i}>
                      <TableCell><Chip label={err.row} color="error" size="small" variant="outlined" /></TableCell>
                      <TableCell><Typography variant="body2" color="error">{err.message}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {importResults?.success_count > 0 && !importResults?.error_count && (
            <Alert severity="success">All marks imported successfully!</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResults(null)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentMarks;
